import type {
  BankSource,
  BidStatus,
  DeliveryPreference,
  IntakeStatus,
  LegalForm,
  ReportingNeed,
  ValidationCode,
  VatPeriod
} from "../lib/validation";
import type { Locale } from "./types";

type Option<T extends string> = {
  value: T;
  label: string;
};

export type QuoteFieldCopy = {
  label: string;
  help: string;
  placeholder?: string;
};

export type QuoteFormCopy = {
  heading: {
    eyebrow: string;
    title: string;
    summary: string;
    recommendations?: {
      intro: string;
      items: string[];
      summary: string;
    };
    expandLabel: string;
    collapseLabel: string;
    resultBackLabel: string;
  };
  groups: {
    company: string;
    volume: string;
    scope: string;
    delivery: string;
  };
  fields: {
    companyName: QuoteFieldCopy;
    contactName: QuoteFieldCopy;
    contactEmail: QuoteFieldCopy;
    contactPhone: QuoteFieldCopy;
    registrationNumber: QuoteFieldCopy;
    vatNumber: QuoteFieldCopy;
    legalForm: QuoteFieldCopy;
    businessDescription: QuoteFieldCopy;
    monthlyTransactionCount: QuoteFieldCopy;
    monthlySalesInvoiceCount: QuoteFieldCopy;
    monthlySupplierInvoiceCount: QuoteFieldCopy;
    monthlyReceiptCount: QuoteFieldCopy;
    payrollEmployeeCount: QuoteFieldCopy;
    bankAccountCount: QuoteFieldCopy;
    bankSources: QuoteFieldCopy;
    vatPeriod: QuoteFieldCopy;
    fixedAssetCount: QuoteFieldCopy;
    reportingNeeds: QuoteFieldCopy;
    deliveryPreference: QuoteFieldCopy;
    consent: QuoteFieldCopy;
    marketingOptIn: QuoteFieldCopy;
  };
  options: {
    legalForms: Option<LegalForm>[];
    vatPeriods: Option<VatPeriod>[];
    bankSources: Option<BankSource>[];
    reportingNeeds: Option<ReportingNeed>[];
    deliveryPreferences: Option<DeliveryPreference>[];
    serviceFlags: Array<{
      field: "usesSieMigration" | "needsCleanup" | "needsBokslut" | "needsFixedAssets" | "needsApArLedgers";
      label: string;
      help: string;
    }>;
  };
  actions: {
    submit: string;
    submitting: string;
    reset: string;
  };
  validation: Record<ValidationCode, string>;
  errorSummary: {
    title: string;
    apiTitle: string;
    validationMessage: string;
    retryMessage: string;
  };
  status: {
    idle: string;
    validating: string;
    submitting: string;
    estimate_ready: string;
    error: string;
  };
  result: {
    title: string;
    apiNotice: string;
    pendingNotice: string;
    statusLabel: string;
    rangeLabel: string;
    confidenceLabel: string;
    assumptionsLabel: string;
    packageLabel: string;
    creditsLabel: string;
    tierLabel: string;
    bidLabel: string;
    nextStepsLabel: string;
    includedLabel: string;
    notIncludedLabel: string;
    caseLabel: string;
    caseOpenLabel: string;
    emptyList: string;
    disclaimerFallback: string;
    pendingRange: string;
    pendingConfidence: string;
    pendingPackage: string;
    pendingCredits: string;
    pendingTier: string;
    pendingBidStatus: string;
    monthlyUnit: string;
    setupLabel: string;
    creditMonthUnit: string;
    initialPackLabel: string;
    pendingAssumptions: string[];
    pendingNextSteps: string[];
    caseStatusLabels: Record<IntakeStatus, string>;
    bidStatusLabels: Record<BidStatus, string>;
    bidStatusMessages: Record<BidStatus, string>;
  };
};

export const quoteFormCopy: Record<Locale, QuoteFormCopy> = {
  sv: {
    heading: {
      eyebrow: "Serviceuppskattning",
      title: "Få en preliminär serviceuppskattning.",
      summary:
        "Beskriv bolaget, månadsvolymen, momsperioden, bankkällor, rapportbehov och eventuell migrering eller städning. DynamicBooks ger en icke-bindande servicevy innan slutlig omfattning bekräftas.",
      recommendations: {
        intro: "Varje serviceuppskattning innehåller tre rekommendationer:",
        items: [
          "Rekommenderade bokföringstjänster",
          "Föreslaget programvaruabonnemang",
          "Förväntad månatlig Agent Credit-kapacitet för AI-assisterade bokföringsflöden"
        ],
        summary:
          "Det ger en tydligare bild av både den återkommande tjänsten och den AI-körkapacitet som förväntas för bokföring, momsrapportering, avstämningar, migreringar och rapporteringsbehov."
      },
      expandLabel: "Starta uppskattningsförfrågan",
      collapseLabel: "Stäng uppskattningsförfrågan",
      resultBackLabel: "Tillbaka till formuläret"
    },
    groups: {
      company: "Bolagsuppgifter",
      volume: "Aktivitet och bankkällor",
      scope: "Arbetsomfattning och rapportbehov",
      delivery: "Leveransväg och samtycke"
    },
    fields: {
      companyName: { label: "Bolagsnamn", help: "2-160 tecken.", placeholder: "Exempel AB" },
      contactName: { label: "Kontaktperson", help: "Namn på ansvarig kontakt.", placeholder: "Namn Efternamn" },
      contactEmail: { label: "E-post", help: "Används endast för ärendeöverlämning.", placeholder: "namn@bolag.se" },
      contactPhone: { label: "Telefon", help: "Valfritt.", placeholder: "+46 ..." },
      registrationNumber: { label: "Organisationsnummer", help: "Minst organisationsnummer eller VAT måste anges.", placeholder: "559000-0000" },
      vatNumber: { label: "VAT-nummer", help: "Minst organisationsnummer eller VAT måste anges.", placeholder: "SE559000000001" },
      legalForm: { label: "Bolagsform", help: "Välj okänd om du inte vet." },
      businessDescription: {
        label: "Kort verksamhetsbeskrivning",
        help: "20-2000 tecken. Ta inte med känsliga detaljer.",
        placeholder: "Beskriv verksamhet, underlag, rapportbehov och eventuella riskflaggor."
      },
      monthlyTransactionCount: { label: "Banktransaktioner per månad", help: "Heltal 0-100000.", placeholder: "120" },
      monthlySalesInvoiceCount: { label: "Kundfakturor per månad", help: "Valfritt heltal.", placeholder: "25" },
      monthlySupplierInvoiceCount: { label: "Leverantörsfakturor per månad", help: "Valfritt heltal.", placeholder: "40" },
      monthlyReceiptCount: { label: "Kvitton per månad", help: "Valfritt heltal.", placeholder: "30" },
      payrollEmployeeCount: { label: "Lön: antal personer", help: "Valfritt heltal.", placeholder: "0" },
      bankAccountCount: { label: "Bankkonton/källor", help: "Valfritt heltal.", placeholder: "2" },
      bankSources: { label: "Bankkällor", help: "Välj alla som passar." },
      vatPeriod: { label: "Momsperiod", help: "Välj aktuell period." },
      fixedAssetCount: { label: "Anläggningar", help: "Valfritt antal i register.", placeholder: "0" },
      reportingNeeds: { label: "Rapportbehov", help: "Välj rapporter som ska ingå i omfattningen." },
      deliveryPreference: { label: "Önskad leverans", help: "Vägen kan justeras efter källor och omfattning." },
      consent: {
        label: "Jag godkänner att DynamicBooks behandlar uppgifterna för en preliminär och icke-bindande uppskattning.",
        help: "Krävs innan formuläret skickas."
      },
      marketingOptIn: { label: "Jag vill kunna få uppföljning om DynamicBooks.", help: "Valfritt." }
    },
    options: {
      legalForms: [
        { value: "unknown", label: "Okänd" },
        { value: "aktiebolag", label: "Aktiebolag" },
        { value: "enskild_firma", label: "Enskild firma" },
        { value: "handelsbolag", label: "Handelsbolag" },
        { value: "forening", label: "Förening" },
        { value: "other", label: "Annat" }
      ],
      vatPeriods: [
        { value: "unknown", label: "Okänd" },
        { value: "monthly", label: "Månadsvis" },
        { value: "quarterly", label: "Kvartalsvis" },
        { value: "yearly", label: "Årsvis" },
        { value: "none", label: "Ingen moms" }
      ],
      bankSources: [
        { value: "bank_file", label: "Bankfil" },
        { value: "revolut", label: "Revolut" },
        { value: "manual_upload", label: "Manuell upload" },
        { value: "future_bank_integration", label: "Planerad bankintegration" },
        { value: "unknown", label: "Okänd källa" }
      ],
      reportingNeeds: [
        { value: "vat_report", label: "Momsrapport" },
        { value: "balance_sheet", label: "Balansrapport" },
        { value: "profit_loss", label: "Resultatrapport" },
        { value: "general_ledger", label: "Huvudbok" },
        { value: "journal_list", label: "Verifikationslista" },
        { value: "account_balances", label: "Kontosaldon" },
        { value: "reports_register", label: "Rapportregister" },
        { value: "annual_artifacts", label: "Årsartefakter" },
        { value: "management_package", label: "Managementpaket" }
      ],
      deliveryPreferences: [
        { value: "hybrid", label: "Guidad självservice" },
        { value: "self_orchestrated_agent", label: "Självserviceuppstart" },
        { value: "human_assisted", label: "Utökad uppstart" }
      ],
      serviceFlags: [
        { field: "usesSieMigration", label: "SIE-migrering", help: "Import, export eller dubblettkontroll." },
        { field: "needsCleanup", label: "Städning/ikappföring", help: "Historik, avvikelser eller korrigeringar." },
        { field: "needsBokslut", label: "Bokslut eller årsarbete", help: "Årsrapport eller paketering." },
        { field: "needsFixedAssets", label: "Anläggningar/avskrivning", help: "Register och periodisering." },
        { field: "needsApArLedgers", label: "AP/AR-reskontra", help: "Kund- eller leverantörsreskontra." }
      ]
    },
    actions: {
      submit: "Skicka förfrågan",
      submitting: "Skickar...",
      reset: "Rensa resultat"
    },
    validation: {
      required: "Fältet krävs.",
      identifier_required: "Ange organisationsnummer eller VAT-nummer.",
      identifier_format: "Identifieraren ska ha 6-20 alfanumeriska tecken efter normalisering.",
      email_format: "Ange en giltig e-postadress.",
      phone_format: "Ange telefon med siffror, mellanslag, parenteser, bindestreck eller plus.",
      length: "Längden ligger utanför tillåtet intervall.",
      integer: "Ange ett heltal utan decimaler.",
      range: "Värdet ligger utanför tillåtet intervall.",
      enum: "Valet är inte giltigt.",
      duplicate: "Samma val finns mer än en gång.",
      missing_consent: "Samtycke krävs."
    },
    errorSummary: {
      title: "Kontrollera dessa fält",
      apiTitle: "Förfrågan kunde inte slutföras",
      validationMessage: "Servern avvisade ett eller flera fält. Kontrollera markerade uppgifter och skicka igen.",
      retryMessage: "Uppskattningen kunde inte hämtas just nu. Försök igen utan att ändra URL:en eller lägga till känsliga uppgifter."
    },
    status: {
      idle: "Inte startad",
      validating: "Validerar fält",
      submitting: "Skickar begäran",
      estimate_ready: "Uppskattning klar",
      error: "Åtgärda markerade fält"
    },
    result: {
      title: "Icke-bindande uppskattning",
      apiNotice: "Din uppskattning är klar och offerten skickas till e-postadressen i formuläret. Ärendeåtkomst sparas endast i den här webbläsaren.",
      pendingNotice:
        "Resultatet nedan visar strukturen och väntar på uppskattningsreglerna.",
      statusLabel: "Status",
      rangeLabel: "Intervall",
      confidenceLabel: "Konfidens",
      assumptionsLabel: "Antaganden",
      packageLabel: "Föreslaget paket",
      creditsLabel: "Agent credits",
      tierLabel: "Föreslagen åtkomst",
      bidLabel: "Offertstatus",
      nextStepsLabel: "Nästa steg",
      includedLabel: "Ingår",
      notIncludedLabel: "Ingår inte",
      caseLabel: "Ärende",
      caseOpenLabel: "Öppna ärendestatus",
      emptyList: "Väntar på serverns uppskattningsregler.",
      disclaimerFallback:
        "Detta är en preliminär och icke-bindande uppskattning. Slutligt pris, omfattning och startdatum fastställs efter kontroll av underlag, datakällor och ansvarsnivå.",
      pendingRange: "Väntar på serverns uppskattningsmotor",
      pendingConfidence: "Ej satt innan uppskattningsreglerna är klara",
      pendingPackage: "Paket föreslås efter validerad volym och omfattning",
      pendingCredits: "Kreditbehov visas när servern returnerar uppskattningen",
      pendingTier: "Åtkomstnivå föreslås efter Data Rooms och arbetsintensitet",
      pendingBidStatus: "Offerten förbereds för e-post",
      monthlyUnit: "per månad",
      setupLabel: "uppstart",
      creditMonthUnit: "Agent credits per månad",
      initialPackLabel: "initialt paket",
      pendingAssumptions: [
        "Volym, momsperiod, bankkällor och rapportbehov är mottagna.",
        "SIE, städning, bokslut, lön och riskflaggor kan ändra omfattningen.",
        "Pris och startdatum bekräftas efter kontroll av källor och omfattning."
      ],
      pendingNextSteps: [
        "Granska att uppgifterna är korrekta.",
        "Vänta på uppskattningsmotor och serverkontrakt.",
        "Fortsätt uppstart när offerten har skickats."
      ],
      caseStatusLabels: {
        received: "Förfrågan mottagen",
        estimate_queued: "Uppskattning köad",
        estimate_running: "Uppskattning beräknas",
        estimate_ready: "Uppskattning klar",
        awaiting_customer: "Väntar på kund",
        closed: "Stängt"
      },
      bidStatusLabels: {
        queued: "Bidpaket förbereds",
        generated: "Bidpaket skapat",
        sent: "Bidpaket skickat",
        send_failed: "Offertförfrågan sparad"
      },
      bidStatusMessages: {
        queued: "Din offertförfrågan är sparad och bidpaketet förbereds.",
        generated: "Ditt self-service-bid har skapats och är redo för leverans.",
        sent: "Ditt self-service-bid har skickats till e-postadressen i förfrågan.",
        send_failed: "Din offertförfrågan är sparad. Vi följer upp när bidpaketet är klart."
      }
    }
  },
  en: {
    heading: {
      eyebrow: "Service estimate",
      title: "Get a preliminary service estimate.",
      summary:
        "Describe your company, monthly volume, VAT period, bank sources, reporting needs, and any migration or cleanup work. DynamicBooks gives a non-binding service view before final scope is confirmed.",
      recommendations: {
        intro: "Every service estimate includes three recommendations:",
        items: [
          "Recommended accounting services",
          "Suggested software subscription",
          "Expected monthly Agent Credit capacity for AI-assisted accounting workflows"
        ],
        summary:
          "This gives you a clearer view of both the recurring service and the AI processing capacity expected for your bookkeeping, VAT reporting, reconciliations, migrations, and reporting requirements."
      },
      expandLabel: "Start estimate request",
      collapseLabel: "Close estimate request",
      resultBackLabel: "Back to form"
    },
    groups: {
      company: "Company details",
      volume: "Activity and bank sources",
      scope: "Work scope and reporting needs",
      delivery: "Delivery path and consent"
    },
    fields: {
      companyName: { label: "Company name", help: "2-160 characters.", placeholder: "Example AB" },
      contactName: { label: "Contact name", help: "Responsible contact.", placeholder: "First Last" },
      contactEmail: { label: "Email", help: "Used only for case handoff.", placeholder: "name@company.se" },
      contactPhone: { label: "Phone", help: "Optional.", placeholder: "+46 ..." },
      registrationNumber: { label: "Registration number", help: "Registration or VAT number is required.", placeholder: "559000-0000" },
      vatNumber: { label: "VAT number", help: "Registration or VAT number is required.", placeholder: "SE559000000001" },
      legalForm: { label: "Legal form", help: "Choose unknown if unsure." },
      businessDescription: {
        label: "Short business description",
        help: "20-2000 characters. Do not include sensitive details.",
        placeholder: "Describe the business, source material, reporting needs, and any scope notes."
      },
      monthlyTransactionCount: { label: "Bank transactions per month", help: "Integer 0-100000.", placeholder: "120" },
      monthlySalesInvoiceCount: { label: "Sales invoices per month", help: "Optional integer.", placeholder: "25" },
      monthlySupplierInvoiceCount: { label: "Supplier invoices per month", help: "Optional integer.", placeholder: "40" },
      monthlyReceiptCount: { label: "Receipts per month", help: "Optional integer.", placeholder: "30" },
      payrollEmployeeCount: { label: "Payroll people", help: "Optional integer.", placeholder: "0" },
      bankAccountCount: { label: "Bank accounts/sources", help: "Optional integer.", placeholder: "2" },
      bankSources: { label: "Bank sources", help: "Select all that apply." },
      vatPeriod: { label: "VAT period", help: "Choose the active period." },
      fixedAssetCount: { label: "Fixed assets", help: "Optional count in the register.", placeholder: "0" },
      reportingNeeds: { label: "Reporting needs", help: "Select reports that should be in scope." },
      deliveryPreference: { label: "Preferred delivery", help: "The path may adjust after sources and scope are checked." },
      consent: {
        label: "I agree that DynamicBooks may process these details for a preliminary and non-binding estimate.",
        help: "Required before submission."
      },
      marketingOptIn: { label: "I may receive follow-up about DynamicBooks.", help: "Optional." }
    },
    options: {
      legalForms: [
        { value: "unknown", label: "Unknown" },
        { value: "aktiebolag", label: "Limited company" },
        { value: "enskild_firma", label: "Sole trader" },
        { value: "handelsbolag", label: "Trading partnership" },
        { value: "forening", label: "Association" },
        { value: "other", label: "Other" }
      ],
      vatPeriods: [
        { value: "unknown", label: "Unknown" },
        { value: "monthly", label: "Monthly" },
        { value: "quarterly", label: "Quarterly" },
        { value: "yearly", label: "Yearly" },
        { value: "none", label: "No VAT" }
      ],
      bankSources: [
        { value: "bank_file", label: "Bank file" },
        { value: "revolut", label: "Revolut" },
        { value: "manual_upload", label: "Manual upload" },
        { value: "future_bank_integration", label: "Planned bank integration" },
        { value: "unknown", label: "Unknown source" }
      ],
      reportingNeeds: [
        { value: "vat_report", label: "VAT report" },
        { value: "balance_sheet", label: "Balance sheet" },
        { value: "profit_loss", label: "Profit and loss" },
        { value: "general_ledger", label: "General ledger" },
        { value: "journal_list", label: "Journal list" },
        { value: "account_balances", label: "Account balances" },
        { value: "reports_register", label: "Reports register" },
        { value: "annual_artifacts", label: "Annual artifacts" },
        { value: "management_package", label: "Management package" }
      ],
      deliveryPreferences: [
        { value: "hybrid", label: "Guided self-service" },
        { value: "self_orchestrated_agent", label: "Self-service onboarding" },
        { value: "human_assisted", label: "Extended onboarding" }
      ],
      serviceFlags: [
        { field: "usesSieMigration", label: "SIE migration", help: "Import, export, or duplicate checks." },
        { field: "needsCleanup", label: "Cleanup/catch-up", help: "History, discrepancies, or corrections." },
        { field: "needsBokslut", label: "Year-end or annual work", help: "Annual report or package prep." },
        { field: "needsFixedAssets", label: "Fixed assets/depreciation", help: "Register and period handling." },
        { field: "needsApArLedgers", label: "AP/AR ledgers", help: "Customer or supplier ledgers." }
      ]
    },
    actions: {
      submit: "Send request",
      submitting: "Submitting...",
      reset: "Clear result"
    },
    validation: {
      required: "This field is required.",
      identifier_required: "Enter a registration number or VAT number.",
      identifier_format: "The identifier must have 6-20 alphanumeric characters after normalization.",
      email_format: "Enter a valid email address.",
      phone_format: "Use digits, spaces, parentheses, hyphen, or plus.",
      length: "The length is outside the allowed range.",
      integer: "Enter an integer without decimals.",
      range: "The value is outside the allowed range.",
      enum: "This selection is not valid.",
      duplicate: "The same selection appears more than once.",
      missing_consent: "Consent is required."
    },
    errorSummary: {
      title: "Check these fields",
      apiTitle: "Request could not finish",
      validationMessage: "The server rejected one or more fields. Check the marked details and submit again.",
      retryMessage: "The estimate could not be retrieved right now. Try again without changing the URL or adding sensitive details."
    },
    status: {
      idle: "Not started",
      validating: "Validating fields",
      submitting: "Sending request",
      estimate_ready: "Estimate ready",
      error: "Fix the marked fields"
    },
    result: {
      title: "Non-binding estimate",
      apiNotice: "Your estimate is ready and the bid is being sent to the email address in the form. Case access is saved only in this browser.",
      pendingNotice:
        "If the estimate service is unavailable, this panel shows the response structure and waits for the estimate rules.",
      statusLabel: "Status",
      rangeLabel: "Range",
      confidenceLabel: "Confidence",
      assumptionsLabel: "Assumptions",
      packageLabel: "Suggested package",
      creditsLabel: "Agent credits",
      tierLabel: "Suggested access",
      bidLabel: "Bid status",
      nextStepsLabel: "Next steps",
      includedLabel: "Included",
      notIncludedLabel: "Not included",
      caseLabel: "Case",
      caseOpenLabel: "Open case status",
      emptyList: "Waiting for server estimate rules.",
      disclaimerFallback:
        "This is a preliminary and non-binding estimate. Final price, scope, and start date are confirmed after source and scope checks.",
      pendingRange: "Waiting for the server estimate engine",
      pendingConfidence: "Not set before estimate rules are ready",
      pendingPackage: "Package suggested after validated volume and scope",
      pendingCredits: "Credit usage appears when the server returns the estimate",
      pendingTier: "Access tier suggested after data-room and work intensity checks",
      pendingBidStatus: "The bid is being prepared for email",
      monthlyUnit: "per month",
      setupLabel: "setup",
      creditMonthUnit: "Agent credits per month",
      initialPackLabel: "initial pack",
      pendingAssumptions: [
        "Volume, VAT period, bank sources, and reporting needs were captured.",
        "SIE, cleanup, year-end, payroll, and risk flags can change scope.",
        "Price and start date are confirmed after source and scope checks."
      ],
      pendingNextSteps: [
        "Check that the details are accurate.",
        "Wait for the server estimate response.",
        "Continue onboarding when the bid has been sent."
      ],
      caseStatusLabels: {
        received: "Request received",
        estimate_queued: "Estimate queued",
        estimate_running: "Estimate running",
        estimate_ready: "Estimate ready",
        awaiting_customer: "Waiting for customer",
        closed: "Closed"
      },
      bidStatusLabels: {
        queued: "Bid package preparing",
        generated: "Bid package generated",
        sent: "Bid package sent",
        send_failed: "Bid request saved"
      },
      bidStatusMessages: {
        queued: "Your bid request is saved and the bid package is being prepared.",
        generated: "Your self-service bid has been generated and is ready for delivery.",
        sent: "Your self-service bid has been sent to the email address in the request.",
        send_failed: "Your bid request is saved. We will follow up when the bid package is ready."
      }
    }
  }
};
